import { addToast } from '@app/components/various/Toasts';
import { AccountRequestStatus } from '@app/constants/requests';
import { setAccountRequestStatus } from '@app/services/accountRepository';
import { ACCOUNT_REQUEST_DETAILS_QUERY_KEY } from '@app/services/hooks/account/useAccountRequestDetails';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export const ACCOUNT_REQUEST_STATUS_QUERY_KEY = 'accountRequestStatus';

interface Params {
  id: string;
  companyName: string;
  status: AccountRequestStatus;
}

export const useAccountRequestStatus = () => {
  const { t } = useTranslation(['requests']);
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, status }: Params) => {
    return setAccountRequestStatus(id, status);
  };

  const notificationMessage = (status: AccountRequestStatus, companyName: string) => {
    switch (status) {
      case AccountRequestStatus.Accepted:
        return t('requests:account_request_status_accepted', { companyName });
      case AccountRequestStatus.Declined:
        return t('requests:account_request_status_declined', { companyName });
      case AccountRequestStatus.Archived:
        return t('requests:account_request_status_archived', { companyName });
      case AccountRequestStatus.Pending:
        return t('requests:account_request_status_pending', { companyName });
      default:
        return '';
    }
  };

  const onSuccess = (_: unknown, { id, companyName, status }: Params) => {
    const notification = notificationMessage(status, companyName);
    addToast(notification);

    queryClient.invalidateQueries({
      queryKey: [ACCOUNT_REQUEST_DETAILS_QUERY_KEY, { id }],
    });
  };

  const onError = () => {
    addToast(t('requests:account_request_status_failed'));
  };

  return useMutation({
    mutationFn,
    onError,
    onSuccess,
  });
};
